import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Landing } from "@/pages/Landing";
import { Billing } from "@/pages/Billing";
import { Dashboard } from "@/pages/Dashboard";
import { Document } from "@/pages/Document";
import { Documents } from "@/pages/Documents";
import { GettingStarted } from "@/pages/GettingStarted";
import { Login } from "@/pages/Login";
import { Onboarding } from "@/pages/Onboarding";
import { Privacy } from "@/pages/Privacy";
import { ReviewerTestPlan } from "@/pages/ReviewerTestPlan";
import { Register } from "@/pages/Register";
import { ResetPassword } from "@/pages/ResetPassword";
import { Settings } from "@/pages/Settings";
import { Sources } from "@/pages/Sources";
import { Terms } from "@/pages/Terms";
import { Tokens } from "@/pages/Tokens";
import { VerifyEmail } from "@/pages/VerifyEmail";
import { Ask } from "@/pages/qa/Ask";
import { Questions } from "@/pages/qa/Questions";
import { Tags } from "@/pages/qa/Tags";
import { TagDetail } from "@/pages/qa/TagDetail";

/**
 * Top-level shell. Routes are split between:
 *
 * - **Public** (rendered to anonymous + authed alike):
 *     /                — Question feed (Phase 3 IA reshuffle — everyone
 *                        lands here, anon or authed)
 *     /questions       — Same as /, kept as a discoverable named route
 *     /tags, /tags/:slug, /ask  — Q&A surfaces
 *     /documents       — Collaborative-doc catalog (non-qa kinds)
 *     /d/:docId        — Document view (the page itself decides whether
 *                        to render <QuestionPage> or the editor based on
 *                        `doc.kind`)
 *     /login, /register, /verify-email, /reset-password, /privacy,
 *     /terms, /getting-started, /reviewer-test-plan
 *
 * - **Authed-only** (gated by `<RequireAuth>`):
 *     /dashboard, /onboarding, /tokens, /settings, /billing
 *     /d/:docId/sources
 */
/**
 * Root route. Signed-in users get their dashboard — their own documents are
 * what they came for, and the public Q&A catalogue is a browsing surface, not
 * a home page. Anonymous visitors still land on the catalogue.
 *
 * Waits for auth to resolve before deciding: rendering the catalogue during
 * the loading tick and then redirecting produces a visible flash and an extra
 * history entry.
 */
function Home() {
  const { state } = useAuth();
  if (state.status === "loading") return null;
  if (state.status === "authenticated") return <Navigate to="/dashboard" replace />;
  return <Landing />;
}


export function App() {
  const { state } = useAuth();

  if (state.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading…
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Phase 3 IA: /questions is the frontpage for everyone (anon + authed). */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/reviewer-test-plan" element={<ReviewerTestPlan />} />
        {/* Q&A surfaces — anonymous-readable. */}
        <Route path="/questions" element={<Questions />} />
        <Route path="/tags" element={<Tags />} />
        <Route path="/tags/:slug" element={<TagDetail />} />
        <Route path="/ask" element={<Ask />} />
        {/* Collaborative-doc catalog (Phase 3) — anonymous-readable. */}
        <Route path="/documents" element={<Documents />} />
        <Route path="/d/:docId" element={<Document />} />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <Onboarding />
            </RequireAuth>
          }
        />
        <Route
          path="/tokens"
          element={
            <RequireAuth>
              <Tokens />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        <Route
          path="/billing"
          element={
            <RequireAuth>
              <Billing />
            </RequireAuth>
          }
        />
        <Route
          path="/d/:docId/sources"
          element={
            <RequireAuth>
              <Sources />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/** Wraps a page so that anonymous users get bounced to /login first.
 * The `from` query param remembers where they were heading so we can
 * send them back after sign-in. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status !== "authenticated") {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?from=${from}`} replace />;
  }
  return <>{children}</>;
}
